import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Drawer, Empty, List, message, Skeleton, Space, Tag, Typography } from "antd";
import {
  BankOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  ReloadOutlined,
  SolutionOutlined,
  TeamOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import SidePromotionSection from "components/promotions/SidePromotionSection";
import paths from "routes/paths";
import { olreadiaService } from "services/olreadiaService";
import "./OlreadiaFlowPages.css";

const { Paragraph, Text, Title } = Typography;
const PAGE_SIZE = 20;
const SORT_OPTIONS = [
  { key: "DATETIME", label: "Date", icon: <ClockCircleOutlined /> },
  { key: "COMPANY", label: "Company", icon: <BankOutlined /> },
  { key: "TITLE", label: "Title", icon: <SolutionOutlined /> },
];

const titleCase = (value) =>
  String(value || "")
    .replace(/[_-]/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number") return String(value);
  }
  return "";
};

const formatDateTime = (value) => {
  if (!value) return "Date not set";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatDuration = (value) => {
  const minutes = Number(value);
  if (!minutes || minutes <= 0) return "";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining ? `${hours} hr ${remaining} min` : `${hours} hr`;
};

const interviewersText = (value) => {
  if (!value) return "";
  const list = Array.isArray(value) ? value : [value];
  return list
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") return firstText(item.name, item.fullName, item.email, item.title, item.id);
      return "";
    })
    .filter(Boolean)
    .join(", ");
};

const statusTone = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "COMPLETED") return "good";
  if (normalized === "CANCELLED" || normalized === "CANCELED") return "bad";
  return "active";
};

const feedbackTone = (status) => {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "PASSED") return "green";
  if (normalized === "FAILED") return "red";
  return "default";
};

const sortText = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "not set" ? "" : normalized;
};

const interviewSortTime = (session) => {
  const time = new Date(session?.interviewDateTime || "").getTime();
  return Number.isFinite(time) ? time : Number.POSITIVE_INFINITY;
};

const jobContext = (item) => {
  const application = item?.jobApplication || {};
  return {
    jobTitle: firstText(application.jobTitle, item.jobTitle, item.jobOpportunityTitle) || "Not set",
    companyName: firstText(application.companyName, item.companyName, item.workspaceName) || "Not set",
  };
};

const jobPostId = (item) => firstText(item?.jobOpportunityId, item?.jobApplication?.jobOpportunityId);
const workspaceId = (item) =>
  firstText(item?.jobApplication?.companyId, item?.companyId, item?.workspaceId, item?.workspace?.id);

const calendarDateValue = (date) => date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");

const interviewCalendarUrl = (item, provider) => {
  const start = new Date(item?.interviewDateTime || "");
  if (!Number.isFinite(start.getTime())) return "";
  const durationMinutes = Number(item.durationMinutes ?? item.duration) || 60;
  const end = new Date(start.getTime() + Math.max(durationMinutes, 15) * 60_000);
  const { jobTitle, companyName } = jobContext(item);
  const meetingLink = item.meetingLink || item.metingLink;
  const title = item.title?.trim() ? `${item.title.trim()} - ${jobTitle}` : `Interview - ${jobTitle}`;
  const details = [
    companyName !== "Not set" ? `Company: ${companyName}` : "",
    item.description,
    item.notesToCandidate ? `Notes: ${item.notesToCandidate}` : "",
    meetingLink ? `Meeting link: ${meetingLink}` : "",
  ].filter(Boolean).join("\n\n");

  if (provider === "OUTLOOK") {
    const params = new URLSearchParams({
      path: "/calendar/action/compose",
      rru: "addevent",
      subject: title,
      startdt: start.toISOString(),
      enddt: end.toISOString(),
    });
    if (details) params.set("body", details);
    if (item.location) params.set("location", item.location);
    return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
  }

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: `${calendarDateValue(start)}/${calendarDateValue(end)}`,
  });
  if (details) params.set("details", details);
  if (item.location) params.set("location", item.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const detailValue = (value) => firstText(value) || "Not set";

export default function ScheduledInterviews() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedWorkspaceId, selectedWorkspaceUse, isIndividualWorkspace } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const [sessions, setSessions] = useState([]);
  const [sortMode, setSortMode] = useState("DATETIME");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastPage, setLastPage] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const pageRef = useRef(0);
  const loadingRef = useRef(false);

  const sortedSessions = useMemo(() => {
    return [...sessions].sort((a, b) => {
      if (sortMode === "COMPANY") {
        const left = jobContext(a);
        const right = jobContext(b);
        return sortText(left.companyName).localeCompare(sortText(right.companyName))
          || sortText(left.jobTitle).localeCompare(sortText(right.jobTitle))
          || sortText(a.title).localeCompare(sortText(b.title));
      }
      if (sortMode === "TITLE") {
        const left = jobContext(a);
        const right = jobContext(b);
        return sortText(left.jobTitle).localeCompare(sortText(right.jobTitle))
          || sortText(a.title).localeCompare(sortText(b.title))
          || sortText(left.companyName).localeCompare(sortText(right.companyName));
      }
      return interviewSortTime(a) - interviewSortTime(b)
        || sortText(a.title).localeCompare(sortText(b.title));
    });
  }, [sessions, sortMode]);

  const loadSessions = useCallback(async ({ reset = false } = {}) => {
    if (!actorId || loadingRef.current) return;
    if (!selectedWorkspaceId) {
      setError("Select a workspace to load interviews.");
      setSessions([]);
      setLastPage(true);
      return;
    }
    loadingRef.current = true;
    setLoading(true);
    const page = reset ? 0 : pageRef.current;
    try {
      const isApplicantWorkspace = isIndividualWorkspace && selectedWorkspaceUse === "JOB_SEARCH";
      const result = isApplicantWorkspace
        ? await olreadiaService.interviewSessions.listForUser({
            actorId,
            userId: actorId,
            workspaceId: selectedWorkspaceId,
            page,
            size: PAGE_SIZE,
          })
        : await olreadiaService.interviewSessions.listForWorkspace({
            actorId,
            workspaceId: selectedWorkspaceId,
            page,
            size: PAGE_SIZE,
          });
      const content = result.content || [];
      setSessions((current) => {
        const merged = reset ? content : [...current, ...content];
        const seen = new Set();
        return merged.filter((item, index) => {
          const id = item.id || `${item.jobApplicationId}-${item.interviewDateTime}-${index}`;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });
      });
      pageRef.current = page + 1;
      setLastPage(Boolean(result.last));
      setError("");
    } catch (nextError) {
      if (reset) setSessions([]);
      setError(nextError?.message || "Could not load interviews.");
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [actorId, isIndividualWorkspace, selectedWorkspaceId, selectedWorkspaceUse]);

  useEffect(() => {
    loadSessions({ reset: true });
  }, [loadSessions]);

  const openCalendar = (item, provider) => {
    const url = interviewCalendarUrl(item, provider);
    if (!url) {
      message.warning("This interview needs a scheduled date before it can be added to a calendar.");
      return;
    }
    window.open(url, "_blank", "noreferrer");
  };

  const renderSession = (item) => {
    const { jobTitle, companyName } = jobContext(item);
    const meetingLink = item.meetingLink || item.metingLink;
    const duration = formatDuration(item.durationMinutes ?? item.duration);
    const itemWorkspaceId = workspaceId(item);
    return (
      <List.Item className="olreadia-flow-list-item">
        <Card className="olreadia-flow-card" hoverable onClick={() => setSelectedSession(item)}>
          <div className="olreadia-flow-card__top">
            <span className="olreadia-flow-card__icon"><CalendarOutlined /></span>
            <div>
              <Title level={4}>{item.title || "Interview session"}</Title>
              <div className="olreadia-flow-detail-row">
                <ClockCircleOutlined /> {formatDateTime(item.interviewDateTime)}
              </div>
              <Space wrap>
                <Tag>{titleCase(item.type || "Interview")}</Tag>
                <Tag>{titleCase(item.interviewMode || "Mode not set")}</Tag>
                {duration ? <Tag>{duration}</Tag> : null}
                {item.interviewFeedbackStatus ? (
                  <Tag color={feedbackTone(item.interviewFeedbackStatus)}>{titleCase(item.interviewFeedbackStatus)}</Tag>
                ) : null}
              </Space>
            </div>
            <Tag className={`olreadia-flow-status is-${statusTone(item.status || "SCHEDULED")}`}>
              {titleCase(item.status || "Scheduled")}
            </Tag>
          </div>
          <div className="olreadia-flow-detail-grid">
            <div>
              <Text>Job</Text>
              <strong>{jobTitle}</strong>
            </div>
            <div>
              <Text>Company</Text>
              <strong>{companyName}</strong>
            </div>
          </div>
          <div className="olreadia-flow-actions">
            {jobPostId(item) ? (
              <Button
                icon={<EyeOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`${paths.jobSearch}?jobOpportunityId=${encodeURIComponent(jobPostId(item))}`);
                }}
              >
                Job post
              </Button>
            ) : null}
            {itemWorkspaceId ? (
              <Button
                icon={<BankOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(paths.workspaceDetails(itemWorkspaceId));
                }}
              >
                Workspace
              </Button>
            ) : null}
            {meetingLink ? (
              <Button
                type="primary"
                icon={<VideoCameraOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  window.open(meetingLink, "_blank", "noreferrer");
                }}
              >
                Meeting
              </Button>
            ) : null}
            <Button
              icon={<CalendarOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                openCalendar(item, "GOOGLE");
              }}
            >
              Google Calendar
            </Button>
          </div>
        </Card>
      </List.Item>
    );
  };

  return (
    <div className="olreadia-flow-page">
      <div className="olreadia-flow-layout">
        <main className="olreadia-flow-main">
          <section className="olreadia-flow-hero">
            <span className="olreadia-flow-hero__icon"><CalendarOutlined /></span>
            <div>
              <Text className="olreadia-flow-eyebrow">Scheduled interviews</Text>
              <Title level={2}>Your Interviews</Title>
              <Paragraph>
                {loading && !sessions.length
                  ? "Loading..."
                  : `${sortedSessions.length} interview session${sortedSessions.length === 1 ? "" : "s"} across your applications and job posts.`}
              </Paragraph>
            </div>
            <Button icon={<ReloadOutlined />} onClick={() => loadSessions({ reset: true })} loading={loading}>
              Refresh
            </Button>
          </section>

          <div className="olreadia-flow-toolbar">
            <div className="olreadia-flow-filter-row">
              {SORT_OPTIONS.map((option) => (
                <Button
                  key={option.key}
                  icon={option.icon}
                  className={`olreadia-flow-chip${sortMode === option.key ? " olreadia-flow-chip--active" : ""}`}
                  onClick={() => setSortMode(option.key)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>

          {error ? (
            <Alert
              type="error"
              showIcon
              message="Unable to load interviews"
              description={error}
              action={<Button onClick={() => loadSessions({ reset: true })}>Retry</Button>}
              style={{ marginBottom: 12 }}
            />
          ) : null}

          <List
            className="olreadia-flow-list"
            loading={loading && !sessions.length}
            dataSource={sortedSessions}
            renderItem={renderSession}
            locale={{
              emptyText: loading ? <Skeleton active /> : <Empty description="No interviews yet" />,
            }}
          />
          {!lastPage && sortedSessions.length ? (
            <Button type="primary" onClick={() => loadSessions({ reset: false })} loading={loading}>
              Load more interviews
            </Button>
          ) : null}
        </main>

        <SidePromotionSection className="olreadia-flow-promotions" />
      </div>

      <Drawer
        open={Boolean(selectedSession)}
        onClose={() => setSelectedSession(null)}
        title={selectedSession?.title || "Interview details"}
        width={560}
        className="olreadia-flow-drawer"
      >
        {selectedSession ? (
          <Space direction="vertical" size={14} className="olreadia-flow-drawer__stack">
            <Space wrap>
              <Tag className={`olreadia-flow-status is-${statusTone(selectedSession.status || "SCHEDULED")}`}>
                {titleCase(selectedSession.status || "Scheduled")}
              </Tag>
              <Tag>{titleCase(selectedSession.type || "Interview")}</Tag>
              <Tag>{titleCase(selectedSession.interviewMode || "Mode not set")}</Tag>
            </Space>
            <div className="olreadia-flow-detail-grid">
              <div><Text>Date</Text><strong>{formatDateTime(selectedSession.interviewDateTime)}</strong></div>
              <div><Text>Duration</Text><strong>{formatDuration(selectedSession.durationMinutes ?? selectedSession.duration) || "Not set"}</strong></div>
              <div><Text>Job</Text><strong>{jobContext(selectedSession).jobTitle}</strong></div>
              <div><Text>Company</Text><strong>{jobContext(selectedSession).companyName}</strong></div>
              <div><Text>Location</Text><strong>{detailValue(selectedSession.location)}</strong></div>
              <div><Text>Interviewers</Text><strong>{detailValue(interviewersText(selectedSession.interviewers))}</strong></div>
            </div>
            {selectedSession.description ? <Paragraph>{selectedSession.description}</Paragraph> : null}
            {selectedSession.notesToCandidate ? <Paragraph>{selectedSession.notesToCandidate}</Paragraph> : null}
            {selectedSession.interviewFeedbackToCandidate ? <Paragraph>{selectedSession.interviewFeedbackToCandidate}</Paragraph> : null}
            <div className="olreadia-flow-actions">
              {(selectedSession.meetingLink || selectedSession.metingLink) ? (
                <Button type="primary" icon={<VideoCameraOutlined />} href={selectedSession.meetingLink || selectedSession.metingLink} target="_blank">
                  Open meeting link
                </Button>
              ) : null}
              <Button icon={<CalendarOutlined />} onClick={() => openCalendar(selectedSession, "GOOGLE")}>
                Google Calendar
              </Button>
              <Button icon={<CalendarOutlined />} onClick={() => openCalendar(selectedSession, "OUTLOOK")}>
                Outlook Calendar
              </Button>
            </div>
          </Space>
        ) : null}
      </Drawer>
    </div>
  );
}
